<script setup lang="ts">
import { computed } from 'vue'
import { equipIcon, equipTypeList } from '@/views/item/data'
import { equipCollection } from '@/core/equip'
import { getEquipName } from '@/utils/equip'
import type { OtherUserInfoType } from '@/types/fight'
import EquipInfo from '@/views/item/components/EquipInfo.vue'
import { dayjs } from 'element-plus'
import { ref } from 'vue'
import { getRuneName } from '@/core/rune'
import RuneInfo from '@/views/item/components/RuneInfo.vue'
import { getRelicName } from '@/core/relic'
import RelicInfo from '@/views/item/components/RelicInfo.vue'
import { getSkillName } from '@/core/skill'
import SkillInfo from '@/views/item/components/SkillInfo.vue'
import type { BeforePeakEquipType, EquipType } from '@/types/equip'

const props = defineProps<{ userInfo: OtherUserInfoType }>()

const showType = ref('equip')
const equipInfoList = computed(() => {
  if (!props.userInfo.equipList) return undefined
  const res = props.userInfo.equipList?.map((item) => {
    if (!item) return undefined
    const { name, type, attrs } = equipCollection[item.equipId]
    return { ...item, name, type, attrs }
  })
  const typeOrder =
    (props.userInfo.equipList?.length || 0) > 5
      ? ['weapon', 'helmet', 'armor', 'belt', 'glove', 'pants', 'shoes', 'necklace', 'jewelry', 'medal']
      : ['weapon', 'helmet', 'armor', 'shoes', 'jewelry']
  return typeOrder.map((type) => {
    return res?.find((item) => item?.type === type)
  })
})
</script>

<template>
  <div class="other-user-info">
    <h3>玩家信息</h3>
    <div class="info">
      <div class="player-info border-wrap">
        <h4>角色信息</h4>
        <p><span>用户ID：</span>{{ userInfo.userId || '(已隐藏)' }}</p>
        <p>
          <span>昵称：</span><span :style="{ color: userInfo.nicknameColor }">{{ userInfo.nickname }}</span>
        </p>
        <p>
          <span>会员等级：</span><span>{{ typeof userInfo.vipLevel === 'number' ? userInfo.vipLevel : '(已隐藏)' }}</span>
        </p>
        <p>
          <span>注册时间：</span><span>{{ userInfo.created ? dayjs(userInfo.created).format('YYYY-MM-DD') : '(已隐藏)' }}</span>
        </p>
      </div>
      <div class="border-wrap equip-list">
        <h4>
          <span :style="{ opacity: showType === 'equip' ? '1' : '0.5' }" @click="showType = 'equip'">装备</span>
          <span> | </span>
          <span :style="{ opacity: showType === 'rune' ? '1' : '0.5' }" @click="showType = 'rune'">符石</span>
          <span> | </span>
          <span :style="{ opacity: showType === 'relic' ? '1' : '0.5' }" @click="showType = 'relic'">圣物</span>
          <span> | </span>
          <span :style="{ opacity: showType === 'skill' ? '1' : '0.5' }" @click="showType = 'skill'">技能</span>
        </h4>
        <p style="text-align: center" v-show="!equipInfoList">（已隐藏）</p>
        <div class="btn-wrap item-btn-wrap" v-show="showType === 'equip'">
          <el-popover
            v-for="(item, index) of equipInfoList"
            placement="left"
            width="50%"
            popper-style="max-width: 350px"
            trigger="click"
            :disabled="!item"
            :teleported="false"
          >
            <template #reference>
              <CommonBtn :key="index" size="small" :type="item ? 'primary' : 'info'">
                <span v-if="item" v-html="getEquipName(item, item.type)"></span>
                <span v-else>{{ equipIcon[equipTypeList[index]] }} (未装备)</span>
              </CommonBtn>
            </template>
            <template v-if="item">
              <EquipInfo :equip-info="item" :show-action="false" :show-extra="false" style="max-height: 70vh" />
            </template>
          </el-popover>
        </div>
        <div class="btn-wrap item-btn-wrap" v-show="showType === 'rune'">
          <el-popover
            v-for="(item, index) of props.userInfo.runeList"
            placement="left"
            width="50%"
            popper-style="max-width: 350px"
            trigger="click"
            :disabled="!item"
          >
            <template #reference>
              <CommonBtn :key="index" size="small" :type="item ? 'primary' : 'info'">
                <span v-if="item" :class="item.quality" v-html="getRuneName(item.runeId)"></span>
                <span v-else>（未装备）</span>
              </CommonBtn>
            </template>
            <template v-if="item">
              <RuneInfo :rune-info="item" :show-action="false" :InItems="false" />
            </template>
          </el-popover>
        </div>
        <div class="btn-wrap item-btn-wrap" v-show="showType === 'relic'">
          <el-popover
            v-for="(item, index) of props.userInfo.relicList"
            placement="left"
            width="50%"
            popper-style="max-width: 350px"
            trigger="click"
            :disabled="!item"
          >
            <template #reference>
              <CommonBtn :key="index" size="small" :type="item ? 'primary' : 'info'">
                <span v-if="item" :class="item.quality" v-html="getRelicName(item.relicId, item.count)"></span>
                <span v-else>（未装备）</span>
              </CommonBtn>
            </template>
            <template v-if="item">
              <RelicInfo :relic-info="item" :show-action="false" :InItems="false" />
            </template>
          </el-popover>
        </div>
        <div class="btn-wrap item-btn-wrap" v-show="showType === 'skill'">
          <el-popover
            v-for="(item, index) of props.userInfo.skillList"
            placement="left"
            width="50%"
            popper-style="max-width: 350px"
            trigger="click"
            :disabled="!item"
          >
            <template #reference>
              <CommonBtn :key="index" size="small" :type="item ? 'primary' : 'info'">
                <span v-if="item" :class="item.quality" v-html="getSkillName(item, false, 0)"></span>
                <span v-else>（未携带）</span>
              </CommonBtn>
            </template>
            <template v-if="item">
              <SkillInfo :skill-info="item" :show-action="false" />
            </template>
          </el-popover>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.other-user-info {
  overflow: hidden;
  .info {
    width: 100%;
    display: flex;
    justify-content: space-between;
    overflow: hidden;
    & > div {
      width: calc(50% - 2.5px);
      flex-shrink: 0;
    }
    h4 {
      width: 100%;
      display: inline-block;
      font-size: 14px;
      text-align: center;
      margin: 10px 0;
    }
  }
  .border-wrap {
    padding: 0 5px 5px 5px;
  }
  .player-info {
    p {
      display: flex;
      justify-content: space-between;
      margin: 14px 0;
    }
  }
  :deep() {
    .el-button:focus,
    .el-button:hover {
      background-color: unset;
    }
  }
  .btn-wrap {
    & > div {
      margin-bottom: 10px;
    }
  }
}
</style>
